"use client";

import { FC, useMemo } from "react";
import { useLocale } from "next-intl";
import { ResponsiveImageType, Image as DatoImage } from "react-datocms";
import { StructuredText } from "components/sections/RichtextSection/StructuredText";
import { Icon } from "components/atoms/Icon/Icon";
import { ButtonRecord, IconType } from "@types";

import { Container } from "components/organisms/Container/Container";
import { Section } from "components/molecules/Section/Section";
import { CBButton } from "components/InlineBlocks/CBButton";
import s from "./ServiceHero.module.scss";
import { Shapes } from "../Shapes/Shapes";
import { BE_LOCALES, type Locale } from "consts/locale";

interface Props {
  image: ResponsiveImageType | undefined;
  customLogo?: ResponsiveImageType;
  intro?: any;
  callToActions?: ButtonRecord[];
  title: string;
  hiddenTitle?: boolean;
  hasPlaceHolderLogo?: boolean;
}

export const ServiceHero: FC<Props> = ({
  image,
  customLogo,
  intro,
  callToActions,
  title,
  hasPlaceHolderLogo,
}) => {
  const locale = useLocale() as Locale;

  const heroLogo = useMemo(() => {
    if (!BE_LOCALES.includes(locale)) {
      return null;
    }

    if (customLogo) {
      return <DatoImage objectFit="contain" data={customLogo} />;
    }

    if (hasPlaceHolderLogo) {
      return <Icon name={IconType.LogoAdvisory} />;
    }

    return null;
  }, [customLogo, hasPlaceHolderLogo, locale]);

  return (
    <>
      <div className={s.Wrapper}>
        <Shapes>
          {image && <DatoImage className={s.Image} objectFit="cover" data={image} priority />}
        </Shapes>
        {heroLogo && (
          <div className={s.LogoContainer}>
            <Container>
              <div className={s.LogoWrapper}>{heroLogo}</div>
            </Container>
          </div>
        )}
      </div>
      <Container size="s">
        <Section>
          <div className={s.IntroWrapper}>
            <h1 className={s.Title}>{title}</h1>

            {intro && (
              <div className={s.Intro}>
                <StructuredText structuredText={intro as any} />
              </div>
            )}
            {callToActions && callToActions.length > 0 && (
              <div className={s.Buttons}>
                {callToActions?.map((callToAction) => (
                  <CBButton key={callToAction.id} {...callToAction} />
                ))}
              </div>
            )}
          </div>
        </Section>
      </Container>
    </>
  );
};
